import { useState, useRef, useEffect } from "react";
import { Sparkle, CircleNotch, CaretDown, PaperPlaneRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LockNotice from "@/components/LockNotice";

const CARD_SUGGESTIONS = [
  "Sharpen this card's framing",
  "Add two more concrete action steps",
  "Make the existing steps more specific",
];

// In-card "Refine with Atlas" — a collapsible, card-scoped chat that lets the user
// refine THIS card's title/justification/steps in place. Step completion ticks are
// preserved server-side. Strictly planning — no owners, no due dates.
export default function CardRefine({ planId, card, onCardsUpdate, lockReason }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  // Reset transient UI when switching to a different card.
  useEffect(() => { setReply(null); setInput(""); setOpen(false); }, [card?._id]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setReply(null);
    setSending(true);
    try {
      const scope = { type: "card", ref_id: card._id, label: card.title };
      const { job_id } = await api.chat(planId, msg, scope);
      const start = Date.now();
      pollRef.current = setInterval(async () => {
        const res = await api.chatJob(planId, job_id).catch(() => null);
        if (res && res.status === "ready") {
          clearInterval(pollRef.current);
          if (res.cards) onCardsUpdate(res.cards);
          setReply(res.reply || "Done.");
          setSending(false);
        } else if ((res && res.status === "error") || Date.now() - start > 120000) {
          clearInterval(pollRef.current);
          toast.error("Atlas could not refine this card.");
          setSending(false);
        }
      }, 2500);
    } catch (e) {
      toast.error("Atlas could not refine this card.");
      setSending(false);
    }
  };

  if (lockReason) {
    return (
      <section className="mt-7 pt-6 border-t border-gray-200" data-testid="card-refine">
        <LockNotice reason={lockReason} />
      </section>
    );
  }

  return (
    <section className="mt-7 pt-6 border-t border-gray-200" data-testid="card-refine">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="card-refine-toggle"
        className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 bg-gray-950 text-white transition-colors group ${open ? "" : "hover:bg-gray-800"}`}
      >
        <span className="flex items-center gap-3 text-left">
          <span className="shrink-0 w-8 h-8 flex items-center justify-center bg-white/10 group-hover:bg-white/20 transition-colors">
            <Sparkle size={18} weight="fill" className="text-amber-300" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-sm font-bold tracking-tight">Refine with Atlas</span>
            <span className="text-[11px] text-gray-300 font-normal">Ask AI to sharpen or extend this card</span>
          </span>
        </span>
        <CaretDown size={18} weight="bold" className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border border-gray-200 border-t-0 p-4 bg-gray-50/60" data-testid="card-refine-panel">
          <div className="flex flex-wrap gap-2 mb-3">
            {CARD_SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                disabled={sending}
                data-testid="card-refine-suggestion"
                className="text-xs text-gray-700 border border-gray-200 bg-gray-50 px-2.5 py-1.5 hover:border-gray-900 disabled:opacity-40 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <textarea
              data-testid="card-refine-input"
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Tell Atlas how to refine this card…"
              className="flex-1 resize-none border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              data-testid="card-refine-send"
              className="bg-gray-950 text-white px-3 disabled:opacity-40 hover:bg-gray-700 transition-colors self-stretch flex items-center"
            >
              {sending ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneRight size={18} weight="fill" />}
            </button>
          </div>

          {sending && (
            <p className="mt-3 flex items-center gap-2 text-sm text-gray-500 font-mono">
              <CircleNotch size={14} className="animate-spin" /> Atlas is refining this card…
            </p>
          )}
          {reply && !sending && (
            <p className="mt-3 text-sm text-gray-700 font-mono border border-gray-200 bg-gray-50 px-3 py-2 leading-relaxed"
              data-testid="card-refine-reply">
              {reply}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
